import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Category, Product } from "@shared/types";
import { api, ApiError } from "@/lib/api";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/Button";
import { TrashIcon } from "@/components/icons";

const emptyForm = {
  name: "",
  slug: "",
  description: "",
  categoryId: "",
  price: "",
  compareAtPrice: "",
  stockQty: "0",
  isActive: true,
  isNew: false,
  requiresDeposit: false,
  depositPercent: "50",
};

export function AdminProductForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();

  const [categories, setCategories] = useState<Category[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.categories.list().then(setCategories);
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    api.admin.products.list().then((all) => {
      const found = all.find((p) => p.id === id);
      if (found) {
        setProduct(found);
        setForm({
          name: found.name,
          slug: found.slug,
          description: found.description,
          categoryId: found.categoryId ?? "",
          price: (found.priceCents / 100).toString(),
          compareAtPrice: found.compareAtPriceCents ? (found.compareAtPriceCents / 100).toString() : "",
          stockQty: found.stockQty.toString(),
          isActive: found.isActive,
          isNew: found.isNew,
          requiresDeposit: found.requiresDeposit,
          depositPercent: (found.depositPercent ?? 50).toString(),
        });
      }
    });
  }, [id, isEdit]);

  const set = <K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name,
      slug: form.slug || undefined,
      description: form.description,
      categoryId: form.categoryId || null,
      priceCents: Math.round(parseFloat(form.price || "0") * 100),
      compareAtPriceCents: form.compareAtPrice ? Math.round(parseFloat(form.compareAtPrice) * 100) : null,
      stockQty: parseInt(form.stockQty || "0", 10),
      isActive: form.isActive,
      isNew: form.isNew,
      requiresDeposit: form.requiresDeposit,
      depositPercent: form.requiresDeposit ? parseInt(form.depositPercent || "50", 10) : null,
    };

    try {
      if (isEdit && id) {
        await api.admin.products.update(id, payload);
        navigate("/admin/products");
      } else {
        const created = await api.admin.products.create(payload as Product);
        navigate(`/admin/products/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!id || !isEdit) return;
    setUploading(true);
    try {
      await api.admin.products.uploadImage(id, file);
      const all = await api.admin.products.list();
      setProduct(all.find((p) => p.id === id) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
      <h1 className="mb-6 font-display text-xl font-medium text-ink">{isEdit ? "Edit Product" : "New Product"}</h1>

      <form onSubmit={handleSubmit} className="grid max-w-2xl gap-4">
        <Field label="Name" value={form.name} onChange={(v) => set("name", v)} required />
        <Field label="Slug (optional — auto-generated from name)" value={form.slug} onChange={(v) => set("slug", v)} />
        <Field label="Description" value={form.description} onChange={(v) => set("description", v)} textarea />

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Category</span>
          <select
            className="rounded-lg border border-stone-300 bg-white px-3.5 py-3 text-sm text-ink"
            value={form.categoryId}
            onChange={(e) => set("categoryId", e.target.value)}
          >
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Price (KES)" value={form.price} onChange={(v) => set("price", v)} type="number" required />
          <Field
            label="Compare-at price (optional)"
            value={form.compareAtPrice}
            onChange={(v) => set("compareAtPrice", v)}
            type="number"
          />
        </div>

        <Field label="Stock quantity" value={form.stockQty} onChange={(v) => set("stockQty", v)} type="number" required />

        <div className="flex flex-wrap gap-6">
          <Checkbox label="Active (visible on storefront)" checked={form.isActive} onChange={(v) => set("isActive", v)} />
          <Checkbox label="Mark as New" checked={form.isNew} onChange={(v) => set("isNew", v)} />
          <Checkbox
            label="Requires deposit"
            checked={form.requiresDeposit}
            onChange={(v) => set("requiresDeposit", v)}
            disabled
          />
        </div>
        <p className="-mt-3 text-xs text-stone-500">
          Disabled until online payment is connected — a deposit forces checkout through a payment provider, and
          none is set up yet.
        </p>

        {form.requiresDeposit && (
          <Field
            label="Deposit percent"
            value={form.depositPercent}
            onChange={(v) => set("depositPercent", v)}
            type="number"
          />
        )}

        {error && <p className="text-sm text-error">{error}</p>}

        <div className="flex gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save Product"}
          </Button>
        </div>
      </form>

      {isEdit && product && (
        <div className="mt-10 max-w-2xl">
          <h2 className="mb-3 font-display text-base font-medium text-ink">Images</h2>
          <p className="mb-3 text-xs text-stone-500">Anything over 5MB is compressed automatically before storage.</p>
          <div className="flex flex-wrap gap-3">
            {product.images.map((img) => (
              <div key={img.id} className="group relative h-24 w-24 overflow-hidden rounded-lg bg-stone-100">
                <img src={img.url} alt={img.alt} className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={async () => {
                    await api.admin.products.removeImage(product.id, img.id);
                    const all = await api.admin.products.list();
                    setProduct(all.find((p) => p.id === product.id) ?? null);
                  }}
                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-error opacity-0 transition group-hover:opacity-100"
                >
                  <TrashIcon width={14} height={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-stone-300 text-xs font-semibold text-stone-500"
            >
              {uploading ? "Uploading…" : "+ Add"}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = "";
              }}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  textarea?: boolean;
}) {
  const className = "rounded-lg border border-stone-300 bg-white px-3.5 py-3 text-sm text-ink focus:border-ink focus:outline-none";
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</span>
      {textarea ? (
        <textarea className={className} rows={4} value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      ) : (
        <input className={className} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} />
      )}
    </label>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-center gap-2 text-sm ${disabled ? "text-stone-500" : "text-ink"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-4 w-4"
      />
      {label}
    </label>
  );
}
